import { Autocomplete, TextField } from '@mui/material';
import React from 'react';

/**
 * SmartAutocomplete — wrapper MUI que substitui o `<Select>` custom bugado
 * usado em quase todos os inspectors do editor Joint (F6.F.1).
 *
 * Contrato compat com o legado: recebe `value`/`onChange`/`options`, emite
 * `onChange(event)` com `event.target.value` (id da opcao). Isso mantem
 * assinatura identica ao Select antigo, permitindo drop-in substitution
 * nos inspectors sem alterar a logica de `changeCellProp`.
 *
 * Fix dos bugs do Select antigo:
 *  - Popper com `flip` habilitado → abre pra cima quando nao cabe pra baixo
 *  - Portal (default MUI) → nao eh cortado pelo overflow do inspector
 *  - Keyboard nav (setas + Enter + Esc) → padrao MUI
 *  - Loading state e vazio semantico
 *  - Sem `onChange` durante digitacao (soh no select real)
 */

type Option = { id: number | string; name: string };

type Props = {
    value: number | string | null;
    onChange: (event: { target: { value: number | string | null } }) => void;
    options: Option[];
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    disableClearable?: boolean;
};

const SmartAutocomplete: React.FC<Props> = ({
    value,
    onChange,
    options,
    label,
    placeholder,
    disabled,
    loading,
    disableClearable,
}) => {
    // Encontra opcao selecionada pelo id — Autocomplete precisa da REFERENCIA do objeto.
    const selected = options.find(o => o.id === value) ?? null;

    return (
        <Autocomplete
            size="small"
            fullWidth
            disabled={disabled}
            loading={loading}
            disableClearable={disableClearable as any}
            options={options}
            value={selected}
            getOptionLabel={(o) => (o as Option)?.name ?? ''}
            isOptionEqualToValue={(a, b) => (a as Option)?.id === (b as Option)?.id}
            onChange={(_, val) => {
                onChange({ target: { value: (val as Option)?.id ?? null } });
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    variant="outlined"
                />
            )}
            slotProps={{
                popper: {
                    modifiers: [
                        { name: 'flip', enabled: true, options: { fallbackPlacements: ['top', 'bottom'] } },
                        { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport' } },
                    ],
                },
            }}
        />
    );
};

export default SmartAutocomplete;
